/**
 * WHICH SPACE this window is in (RFC 0281 §8).
 *
 * The space id lives in the url beside the content tree, so a space is
 * addressable and shareable exactly as a layout already is. `?v=` says what the
 * panes are; `?space=` says which named arrangement they belong to, and the two
 * are set in one navigation so a switch is one history entry rather than two.
 *
 * The rest of this file is one rule: what to open when the url does not say.
 */

import type { SpaceRef } from '@mod/core/model/spaces'

/** The query parameter, beside `CONTENT_PARAM`. */
export const SPACE_PARAM = 'space'

/**
 * `?space=new`: "give this window a space of its own" (RFC 0343 3c).
 *
 * What the desktop's New Window loads. A new window on the space another
 * window is showing would share its Open list (`mod_w_open_v2:<space>`), and
 * the two would overwrite each other's working set; a new space has neither
 * problem. Not a space id: ids are minted `s…`, so this never names one.
 */
export const NEW_SPACE = 'new'

/**
 * Which space this device was last in, PER WORLD.
 *
 * Per device, because where you are is not a shared fact (§7), so it never goes
 * near the spaces document.
 *
 * Per WORLD, because a space id only means anything inside the World that holds
 * it. One unscoped key made two Worlds on one device overwrite each other's
 * place: visiting the second wrote its id over the first's, so going back
 * landed on `order[0]` instead of where you left off, and neither World could
 * ever remember. That is not hypothetical — repointing `mod_newsync_world` at
 * another World is a supported thing to do.
 *
 * Scoped rather than validated-harder on purpose. A stale id from another World
 * was always caught by step 2's lookup and fell through safely; the bug was
 * never a wrong space, it was a FORGOTTEN one, and no amount of validation
 * recovers a value that has been overwritten.
 *
 * NO MIGRATION. The old unscoped `mod_w_space_v1` is simply never read again:
 * the worst it costs is one fallback to `order[0]` on first load after this
 * ships, which is the same thing a new device does.
 */
const LAST_SPACE_PREFIX = 'mod_w_space_v1:'

export const lastSpaceKey = (worldId: string): string =>
  `${LAST_SPACE_PREFIX}${worldId}`

/**
 * The last space for a World, or null.
 *
 * A null `worldId` reads nothing rather than falling back to some other World's
 * value: until the World is known there is no question to answer, and guessing
 * here is what step 4 exists to prevent.
 */
export function readLastSpace(worldId: string | null): string | null {
  if (!worldId) return null
  try {
    return localStorage.getItem(lastSpaceKey(worldId))
  } catch {
    return null
  }
}

export function writeLastSpace(worldId: string | null, spaceId: string): void {
  if (!worldId) return
  try {
    localStorage.setItem(lastSpaceKey(worldId), spaceId)
  } catch {
    // best-effort: a browser refusing storage costs a remembered place, never
    // the window.
  }
}

/**
 * What to do about the current space.
 *
 * `waiting` is its own answer rather than a null, because it is the state that
 * must NOT be mistaken for "there are no spaces": the spaces document is synced,
 * so it is legitimately absent for a moment on every cold start.
 */
export type SpaceResolution =
  | { kind: 'space'; spaceId: string }
  | { kind: 'create' }
  /** `?space=new` against a World that has spaces: make one for this window. */
  | { kind: 'new' }
  | { kind: 'waiting' }

/**
 * Which space to open (RFC 0281 §8), in four steps.
 *
 *   1. `?space=<id>` in the url
 *   2. the device's last opened id, VALIDATED against the document
 *   3. the first id in `order`, which is the World's answer
 *   4. only if the document is loaded and genuinely empty, create one
 *
 * STEP 4 IS THE ONE TO BE CAREFUL ABOUT, and it is why `loaded` is a parameter
 * rather than inferred from an empty list. The project version of this code
 * got it wrong: `stored ?? createProject(...)` meant an empty localStorage
 * silently forked a brand-new empty project and stranded the real one
 * (`WorldPage.tsx` still carries the comment). A cleared browser, a new
 * laptop, a space deleted on another device, and a document that has not arrived
 * yet all reach step 2 empty handed, and NONE of them means "make a new one".
 *
 * STEP 2 IS A LOOKUP, NOT A TRUST. An id can name a space someone deleted
 * elsewhere, and a dangling id is a miss that falls through rather than an
 * error.
 *
 * `?space=new` asks for a space of this window's own (RFC 0343). It waits for
 * the document like any url, and against a World with NO spaces it is step 4:
 * the first space is the window's own already.
 *
 * Step 1 is trusted only while the document is unread. A url naming a space this
 * client cannot see yet should wait for it, not be redirected away from it and
 * then quietly left in the wrong place when it arrives.
 */
export function resolveSpace(input: {
  urlSpaceId: string | null
  lastOpenedId: string | null
  spaces: SpaceRef[]
  /** Has the spaces document actually been read? Absent is not empty. */
  loaded: boolean
}): SpaceResolution {
  const { urlSpaceId, lastOpenedId, spaces, loaded } = input
  const wantsNew = urlSpaceId === NEW_SPACE

  if (!loaded) {
    return urlSpaceId && !wantsNew
      ? { kind: 'space', spaceId: urlSpaceId }
      : { kind: 'waiting' }
  }

  if (wantsNew && spaces.length > 0) return { kind: 'new' }

  const has = (id: string | null): id is string =>
    id !== null && spaces.some((s) => s.spaceId === id)

  if (has(urlSpaceId)) return { kind: 'space', spaceId: urlSpaceId }
  if (has(lastOpenedId)) return { kind: 'space', spaceId: lastOpenedId }

  const first = spaces[0]
  if (first) return { kind: 'space', spaceId: first.spaceId }

  return { kind: 'create' }
}
